import { ConnectionHealthBanner } from '@/components/helpdesk/ConnectionHealthBanner';
import { useDashboardCustomer } from '@/contexts/DashboardCustomerContext';
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Ticket, AlertCircle, AlertTriangle, Info, CheckCircle, ExternalLink } from 'lucide-react';
import { helpdeskApi, TicketSummary } from '@/lib/api/helpdesk';
import { SkeletonLoader } from '@/components/common/SkeletonLoader';
import TicketListModal from './TicketListModal';

interface TicketsWidgetProps {
  refreshTrigger?: number;
  onModalStateChange?: (isOpen: boolean) => void;
}

export const TicketsWidget: React.FC<TicketsWidgetProps> = ({ refreshTrigger, onModalStateChange }) => {
  const { customer_id, customer_name } = useDashboardCustomer();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<TicketSummary | null>(null);
  const [selectedFilter, setSelectedFilter] = useState<{
    priority?: string;
    status?: string;
    status_type?: string;
  } | null>(null);
  const [showModal, setShowModal] = useState(false);

  // Notify parent when modal state changes
  useEffect(() => {
    onModalStateChange?.(showModal);
  }, [showModal, onModalStateChange]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setSummary(null);
    helpdeskApi.getTicketSummary(customer_id)
      .then(data => { if (active) setSummary(data); })
      .catch(() => { if (active) setSummary(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [customer_id, refreshTrigger]);

  const handlePriorityClick = (priority: string) => {
    setSelectedFilter({ priority });
    setShowModal(true);
  };

  const handleStatusClick = (statusType: string) => {
    // Use status_type for Open/Closed categorization
    setSelectedFilter({ status_type: statusType });
    setShowModal(true);
  };

  if (loading) {
    return <SkeletonLoader variant="chart" />;
  }

  if (!summary || summary.total === 0) {
    return (
      <>
      <ConnectionHealthBanner />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-slate-800/95 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-gray-100 dark:bg-slate-700/50">
            <Ticket className="w-6 h-6 text-gray-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Support Tickets
          </h3>
        </div>
        <div className="text-center py-8">
          <Info className="w-12 h-12 text-gray-400 mx-auto mb-2" />
          <p className="text-gray-600 dark:text-gray-400">
            {!summary ? 'Unable to load support tickets' : !summary.mapped ? `No Zoho account linked to ${customer_name}` : `No cached tickets for ${customer_name}`}
          </p>
          <a
            href="/settings/helpdesk"
            className="text-blue-500 hover:text-blue-600 text-sm mt-2 inline-flex items-center gap-1"
          >
            Configure helpdesk integration
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </motion.div>
      </>
    );
  }

  return (
    <>
      <ConnectionHealthBanner />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-slate-800/95 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
              <Ticket className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Support Tickets
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {summary.total} total tickets
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              setSelectedFilter(null);
              setShowModal(true);
            }}
            className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
          >
            View All
            <ExternalLink className="w-3 h-3" />
          </button>
        </div>

        {/* Priority Breakdown */}
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              By Priority
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <PriorityBadge
                priority="Urgent"
                count={summary.urgent_count}
                icon={<AlertCircle className="w-4 h-4" />}
                color="red"
                onClick={() => handlePriorityClick('Urgent')}
              />
              <PriorityBadge
                priority="High"
                count={summary.high_count}
                icon={<AlertTriangle className="w-4 h-4" />}
                color="orange"
                onClick={() => handlePriorityClick('High')}
              />
              <PriorityBadge
                priority="Medium"
                count={summary.by_priority['Medium'] || 0}
                icon={<Info className="w-4 h-4" />}
                color="yellow"
                onClick={() => handlePriorityClick('Medium')}
              />
              <PriorityBadge
                priority="Low"
                count={summary.by_priority['Low'] || 0}
                icon={<CheckCircle className="w-4 h-4" />}
                color="green"
                onClick={() => handlePriorityClick('Low')}
              />
            </div>
          </div>

          {/* Status Breakdown */}
          <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              By Status
            </h4>
            <div className="space-y-2">
              <StatusBar
                label="Open"
                count={summary.open_count}
                total={summary.total}
                color="blue"
                onClick={() => handleStatusClick('Open')}
              />
              <StatusBar
                label="Resolved"
                count={summary.resolved_count}
                total={summary.total}
                color="green"
                onClick={() => handleStatusClick('Resolved')}
              />
              <StatusBar
                label="Closed"
                count={summary.closed_count}
                total={summary.total}
                color="gray"
                onClick={() => handleStatusClick('Closed')}
              />
            </div>
          </div>
        </div>
      </motion.div>

      {/* Ticket List Modal */}
      {showModal && (
        <TicketListModal
          filter={selectedFilter}
          onClose={() => {
            setShowModal(false);
            setSelectedFilter(null);
          }}
          onTicketModalStateChange={onModalStateChange}
        />
      )}
    </>
  );
};

// Priority Badge Component
function PriorityBadge({
  priority,
  count,
  icon,
  color,
  onClick,
}: {
  priority: string;
  count: number;
  icon: React.ReactNode;
  color: 'red' | 'orange' | 'yellow' | 'green';
  onClick: () => void;
}) {
  const colorClasses = {
    red: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30',
    orange: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800 text-orange-700 dark:text-orange-400 hover:bg-orange-100 dark:hover:bg-orange-900/30',
    yellow: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800 text-yellow-700 dark:text-yellow-400 hover:bg-yellow-100 dark:hover:bg-yellow-900/30',
    green: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30',
  };

  return (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={`p-3 rounded-lg border-2 transition-all cursor-pointer ${colorClasses[color]}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-sm font-medium">{priority}</span>
        </div>
        <span className="text-xl font-bold">{count}</span>
      </div>
    </motion.button>
  );
}

// Status Bar Component
function StatusBar({
  label,
  count,
  total,
  color,
  onClick,
}: {
  label: string;
  count: number;
  total: number;
  color: 'blue' | 'green' | 'gray';
  onClick: () => void;
}) {
  const percentage = total > 0 ? (count / total) * 100 : 0;

  const colorClasses = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    gray: 'bg-gray-400',
  };

  return (
    <button
      onClick={onClick}
      className="w-full text-left group hover:bg-gray-50 dark:hover:bg-slate-700/50 p-2 rounded-lg transition-colors"
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white">
          {label}
        </span>
        <span className="text-sm font-bold text-gray-900 dark:text-white">
          {count}
        </span>
      </div>
      <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className={`h-full ${colorClasses[color]}`}
        />
      </div>
    </button>
  );
}

export default TicketsWidget;
